import type * as React from 'react';
import { useCallback, useRef } from 'react';

/**
 * Custom hook to preserve scroll position when messages are prepended to the chat container.
 *
 * When new messages are added to the top of the conversation, the browser automatically
 * scrolls to maintain the DOM position, which causes unwanted jumps. This hook preserves
 * the user's current visual position by adjusting the scroll after new content is added.
 *
 * See more at https://coursera.atlassian.net/browse/COACH-1679
 */
export const useScrollPositionPreservation = (containerRef: React.RefObject<HTMLElement>) => {
  const scrollStateRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
  } | null>(null);

  /**
   * Capture the current scroll state before new messages are added
   * This should be called right before the fetch operation that will prepend messages
   */
  const captureScrollState = useCallback<() => void>(() => {
    if (containerRef.current) {
      scrollStateRef.current = {
        scrollTop: containerRef.current.scrollTop,
        scrollHeight: containerRef.current.scrollHeight,
      };
    }
  }, [containerRef]);

  /**
   * Restore the scroll position after new messages have been added
   * This should be called after the DOM has been updated with the new messages
   */
  const restoreScrollPosition = useCallback<() => void>(() => {
    if (containerRef.current && scrollStateRef.current) {
      const { scrollTop: previousScrollTop, scrollHeight: previousScrollHeight } = scrollStateRef.current;
      const currentScrollHeight = containerRef.current.scrollHeight;

      // Calculate the height difference due to the new messages
      const heightDifference = currentScrollHeight - previousScrollHeight;

      // Adjust scroll position to maintain the user's visual position
      const newScrollTop = Math.max(
        0,
        Math.min(previousScrollTop + heightDifference, currentScrollHeight - containerRef.current.clientHeight)
      );
      containerRef.current.scrollTop = newScrollTop;

      // Reset state
      scrollStateRef.current = null;
    }
  }, [containerRef]);

  return {
    captureScrollState,
    restoreScrollPosition,
  };
};
